import { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { Link, useLoaderData } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import LogoIcon from "~/components/icons/LogoIcon";
import AutoplayCarousel from "~/components/ui/AutoplayCarousel";
import { Button } from "~/components/ui/Button";
import {
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "~/components/ui/Carousel";
import { getLocale } from "~/lib/i18n.server";
import { createSitemapFunction } from "~/lib/sitemap-generator";
import { getMenuHref, getMetaData, isEnabled } from "~/lib/utils";

export const sitemap = createSitemapFunction("about-us");

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return getMetaData(data);
};

export async function loader({ context, request }: LoaderFunctionArgs) {
  try {
    const pageSlug = "chi-siamo";
    const locale = await getLocale(request);
    const lang = locale;
    const res = await fetch(
      `${context.cloudflare.env.WP_API_HOST}/wp-json/wp/v2/pages?slug=${pageSlug}&lang=${lang}`
    );
    const pages: any = await res.json();
    if (!res.ok) {
      throw new Error(`Failed to fetch: ${res.statusText}`);
    }
    if (pages && pages.length > 0) {
      return {
        page: pages[0],
        seo: pages[0]?.yoast_head_json,
        lang,
      };
    } else {
      return {
        error: "No pages found for the given slug.",
        page: {},
      };
    }
  } catch (err) {
    return {
      err: err,
      page: {},
    };
  }
}

export default function AboutUs() {
  const loaderData = useLoaderData<typeof loader>();
  const { t, i18n } = useTranslation();

  return (
    <div
      className={`
        relative
      `}
    >
      <main className="flex flex-col">
        {loaderData.page.acf?.hero && (
          <section
            className="bg-cover bg-center bg-no-repeat "
            style={{
              backgroundImage: `url('/cms-asset/${loaderData.page.acf?.hero.image.id}')`,
            }}
          >
            <div className="bg-black bg-opacity-40 py-32">
              <div className="flex flex-col max-w-4xl mx-auto items-center justify-center">
                <h1 className="text-4xl lg:text-5xl text-white font-tenor-sans uppercase">
                  {loaderData.page.acf?.hero.title}
                </h1>
                <hr className="block w-16 h-[2px] bg-white mt-2" />
                <div
                  className="text-white text-lg mt-4"
                  dangerouslySetInnerHTML={{
                    __html: loaderData.page.acf?.hero.description,
                  }}
                ></div>
              </div>
            </div>
          </section>
        )}

        {loaderData.page &&
          loaderData.page.acf?.first_section &&
          isEnabled(
            loaderData.page.acf?.first_section?.first_section_enable
          ) && (
            <section className="w-full max-w-5xl mx-auto py-12 flex flex-col items-center px-4">
              <LogoIcon className="w-32 lg:w-52 h-20 fill-secondary" />
              <div
                className="text-center mt-4"
                dangerouslySetInnerHTML={{
                  __html: loaderData.page.acf?.first_section.description,
                }}
              />
            </section>
          )}

        {loaderData.page &&
          loaderData.page.acf?.book_section &&
          isEnabled(loaderData.page.acf?.book_section?.book_section_enable) && (
            <section className="w-full">
              <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12 px-4">
                <div>
                  <img
                    alt=""
                    src={`/cms-asset/${loaderData.page.acf?.book_section.image.id}`}
                    className="w-full h-96 object-cover object-center"
                  />
                </div>
                <div className="flex flex-col">
                  <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                    {loaderData.page.acf?.book_section.title}
                  </h4>
                  <div
                    className="mt-4"
                    dangerouslySetInnerHTML={{
                      __html: loaderData.page.acf?.book_section.description,
                    }}
                  />
                </div>
              </div>
            </section>
          )}

        {loaderData.page &&
          loaderData.page.acf?.book_section_2 &&
          isEnabled(
            loaderData.page.acf?.book_section_2?.book_section_2_enable
          ) && (
            <section className="w-full">
              <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12 px-4">
                <div className="flex flex-col">
                  <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                    {loaderData.page.acf?.book_section_2.title}
                  </h4>
                  <div
                    className="mt-4"
                    dangerouslySetInnerHTML={{
                      __html: loaderData.page.acf?.book_section_2.description,
                    }}
                  />
                </div>
                <div>
                  <img
                    alt=""
                    src={`/cms-asset/${loaderData.page.acf?.book_section_2.image.id}`}
                    className="w-full h-96 object-cover object-center"
                  />
                </div>
              </div>
            </section>
          )}

        {loaderData.page &&
          loaderData.page.acf?.book_section_3 &&
          isEnabled(
            loaderData.page.acf?.book_section_3?.book_section_3_enable
          ) && (
            <section className="w-full">
              <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-8 max-w-7xl mx-auto py-12 px-4">
                <div>
                  <img
                    alt=""
                    src={`/cms-asset/${loaderData.page.acf?.book_section_3.image?.id}`}
                    className="w-full h-96 object-cover object-center"
                  />
                </div>
                <div className="flex flex-col">
                  <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                    {loaderData.page.acf?.book_section_3.title}
                  </h4>
                  <div
                    className="mt-4"
                    dangerouslySetInnerHTML={{
                      __html: loaderData.page.acf?.book_section_3.description,
                    }}
                  />
                </div>
              </div>
            </section>
          )}

        {loaderData.page.acf?.spazi_section &&
          isEnabled(
            loaderData.page.acf?.spazi_section?.spazi_section_enable
          ) && (
            <section className="flex flex-col py-12 max-w-7xl mx-auto px-4">
              <h4 className="text-secondary font-tenor-sans text-2xl lg:text-4xl uppercase">
                {loaderData.page.acf?.spazi_section.title}
              </h4>
              <div
                className="mt-4"
                dangerouslySetInnerHTML={{
                  __html: loaderData.page.acf?.spazi_section.description,
                }}
              />

              <AutoplayCarousel
                className="w-full mt-8"
                opts={{
                  align: "start",
                  loop: true,
                  slidesToScroll: 1,
                  breakpoints: {
                    "(min-width: 768px)": {
                      slidesToScroll: 2, // For screens 768px and wider
                    },
                    "(min-width: 1024px)": {
                      slidesToScroll: 3, // For screens 1024px and wider
                    },
                  },
                }}
              >
                <div className="flex flex-row">
                  <CarouselDots className="relative items-end justify-end" />
                  <div className="flex flex-row space-x-2 ml-4">
                    <CarouselPrevious className="relative left-0 right-0 bottom-0 top-0" />
                    <CarouselNext className="relative left-0 right-0 bottom-0 top-0" />
                  </div>
                </div>
                <CarouselContent>
                  {loaderData.page.acf?.spazi_section.images?.map(
                    (_: any, index: number) => (
                      <CarouselItem
                        key={index}
                        className="md:basis-1/2 lg:basis-1/3"
                      >
                        <div className="w-full h-[25rem]">
                          <span className="text-4xl font-semibold">
                            <img
                              alt=""
                              src={`/cms-asset/${_.image.id}`}
                              className="w-full h-full object-cover object-center"
                            />
                          </span>
                        </div>
                      </CarouselItem>
                    )
                  )}
                </CarouselContent>
              </AutoplayCarousel>
            </section>
          )}

        {loaderData.page.acf?.booking_section &&
          isEnabled(
            loaderData.page.acf?.booking_section?.booking_section_enable
          ) && (
            <section className="max-w-7xl mx-auto w-full mb-12">
              <div
                className="bg-cover bg-center bg-no-repeat w-full"
                style={{
                  backgroundImage: `url('/cms-asset/${loaderData.page.acf?.booking_section?.background_image?.id}')`,
                }}
              >
                <div className="bg-black bg-opacity-20 py-20 px-4">
                  <div className="flex flex-col max-w-3xl mx-auto items-center justify-center">
                    <h1 className="text-4xl text-center font-tenor-sans text-white">
                      {loaderData.page.acf?.booking_section.title}
                    </h1>
                    <div
                      className="text-white mt-4 text-xl text-center"
                      dangerouslySetInnerHTML={{
                        __html:
                          loaderData.page.acf?.booking_section.description,
                      }}
                    />
                    <div className="flex items-center justify-center space-x-2">
                      <Link to={getMenuHref(i18n.language, "/booking")}>
                        <Button variant={"muted"} className="mt-4">
                          {t("Prenota")}
                        </Button>
                      </Link>
                      <Link to={getMenuHref(i18n.language, "/shop")}>
                        <Button variant={"muted"} className="mt-4">
                          {t("Regala")}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
      </main>
    </div>
  );
}
